import api from './api'

export type BatchType = 'invoice' | 'claim'

export interface CreateBatchBody {
  batchName: string
  batchMonth: string // "YYYY-MM"
  description?: string
  totalProvider: number
  totalInvoice: number
  totalAmount: number
  providerPayout: number
  totalClaim: number
  batchType: BatchType
  invoiceIds?: string[]
  claimIds?: string[]
}

// POST /batch — create a provider payout batch from selected invoices/claims.
export const createBatch = async (body: CreateBatchBody) => {
  const response = await api.post('/batch', body)
  return response.data
}

export interface ApiBatch {
  _id: string
  agencyId: string | Record<string, any>
  batchName: string
  batchMonth: string // "YYYY-MM"
  description?: string
  status: string
  totalProvider: number
  totalInvoice: number
  totalAmount: number
  providerPayout?: number
  totalClaim: number
  batchType: BatchType
  createdBy?: string | Record<string, any>
  paymentDate?: string
  createdAt: string
  updatedAt: string
}

// createdBy may come back as a string or a populated agency/user object.
export const getBatchCreatedByName = (createdBy: unknown): string => {
  if (!createdBy) return '—'
  if (typeof createdBy === 'string') return createdBy
  if (typeof createdBy === 'object') {
    const c = createdBy as Record<string, any>
    return (
      c.agencyName ||
      c.name ||
      `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
      '—'
    )
  }
  return '—'
}

export interface BatchListResponse {
  success: boolean
  message: string
  data: ApiBatch[]
  total?: number
  page?: number
  limit?: number
  totalPages?: number
  totalAmount?: number
  totalProviderPayout?: number
  totalProviderPayoutPaid?: number
  totalProviderPayoutPending?: number
  pagination?: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface GetBatchesParams {
  page?: number
  limit?: number
  search?: string
  batchMonth?: string
  status?: string
}

// GET /batch — fetch the agency's payout batches.
export const getBatches = async (
  params: GetBatchesParams = {}
): Promise<BatchListResponse> => {
  const response = await api.get<BatchListResponse>('/batch', { params })
  return response.data
}

// DELETE /batch/:id — remove a payout batch.
export const deleteBatch = async (id: string) => {
  const response = await api.delete(`/batch/${id}`)
  return response.data
}

// DELETE /batch/remove-invoice/:batchId/:invoiceId — remove one invoice from a
// batch.
export const removeInvoiceFromBatch = async (
  batchId: string,
  invoiceId: string
) => {
  const response = await api.delete(
    `/batch/remove-invoice/${batchId}/${invoiceId}`
  )
  return response.data
}

// DELETE /batch/remove-claim/:batchId/:claimId — remove one claim from a batch.
export const removeClaimFromBatch = async (
  batchId: string,
  claimId: string
) => {
  const response = await api.delete(`/batch/remove-claim/${batchId}/${claimId}`)
  return response.data
}

// Remove a single line item from a batch, choosing the right endpoint by type.
export const removeBatchItem = async (
  batchId: string,
  itemId: string,
  batchType: BatchType
) =>
  batchType === 'claim'
    ? removeClaimFromBatch(batchId, itemId)
    : removeInvoiceFromBatch(batchId, itemId)

// Turn a blob API response into a browser file download.
const saveBlobResponse = (
  response: { data: BlobPart; headers: Record<string, unknown> },
  fallbackName: string
) => {
  const disposition = response.headers['content-disposition'] as
    | string
    | undefined
  const headerName = disposition?.match(/filename="?([^"]+)"?/)?.[1]
  const type = response.headers['content-type'] as string | undefined
  const extension = type?.includes('csv')
    ? 'csv'
    : type?.includes('sheet')
      ? 'xlsx'
      : 'csv'

  const blob = new Blob([response.data], {
    type: type || 'application/octet-stream',
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = headerName || `${fallbackName}.${extension}`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// GET /batch/:id/export — download a single batch report file.
export const exportBatch = async (id: string, fileName?: string) => {
  const response = await api.get(`/batch/${id}/export`, {
    responseType: 'blob',
  })
  saveBlobResponse(response, fileName?.replace(/\.\w+$/, '') || `batch-${id}`)
}

// GET /batch/batch-export — download all batches (respects list filters) as CSV.
export const exportBatchesCSV = async (params: GetBatchesParams = {}) => {
  const response = await api.get('/batch/batch-export', {
    params: {
      search: params.search || undefined,
      batchMonth: params.batchMonth || undefined,
      status: params.status || undefined,
    },
    responseType: 'blob',
  })
  saveBlobResponse(response, 'payout-batches')
}

export interface BatchInvoiceRef {
  _id: string
  invoice_ids?: string
  patient_name?: string
  // Claim-batch fields
  claimId?: string
  payerName?: string
  patientFirstName?: string
  patientLastName?: string
  totalAmount?: number
  providerPayout?: number
}

export interface BatchSessionRef {
  _id: string
  contract_ids?: string
  session_ids?: string
}

export interface BatchDetailRow {
  _id: string
  batchId: string
  agencyId: string
  providerId: {
    _id: string
    firstName: string
    lastName: string
    email?: string
  }
  invoiceIds: Array<string | BatchInvoiceRef>
  claimIds: Array<string | BatchInvoiceRef>
  sessionIds: Array<string | BatchSessionRef>
  totalAmount: number
  providerPayout?: number
  status: string
}

export interface BatchDetailResponse {
  success: boolean
  message: string
  data: {
    batch: ApiBatch
    batchDetails: BatchDetailRow[]
    totalSessions?: number
  }
}

// GET /batch/:id — single batch with per-provider breakdown.
export const getBatchById = async (
  id: string
): Promise<BatchDetailResponse> => {
  const response = await api.get<BatchDetailResponse>(`/batch/${id}`)
  return response.data
}

// PATCH /batch/:id/status — update a batch's payout status.
export const updateBatchStatus = async (id: string, status: string) => {
  const response = await api.patch(`/batch/${id}/status`, { status })
  return response.data
}
